import { create } from "zustand";

/**
 * Shared "does this site have unsynced changes?" state, scoped per site.
 *
 * Every save flow that changes connector settings (edit camera, products,
 * ignore zones, line crossings, bulk updates, add camera, …) marks the site
 * unsynced here, and every sync flow marks it synced. All sync indicators
 * (the About-page SyncSiteButton, the edit-view EditViewSyncButton, and the
 * SyncNeededBanner) subscribe to this store, so they can never disagree —
 * and because the state is keyed by site id, editing one site never shows a
 * false "sync needed" on another.
 *
 * localStorage ("unsyncedSiteIds" = JSON array) is kept as a mirror only —
 * it preserves the flags across full page reloads. In-memory store state is
 * the source of truth; components must subscribe to the store.
 */

const STORAGE_KEY = "unsyncedSiteIds";

/**
 * Site id from the current URL. Covers both URL shapes:
 * /sites/{id}/... (edit views, add camera) and /sites/aboutsite/{id}.
 * Local to this module (not reusing EditCamera's getNumbersAfterSites) to
 * keep the store free of component imports.
 */
export const getSiteIdFromUrl = (): string => {
  const match = location.href.match(/\/sites\/(?:aboutsite\/)?(\d+)/);
  return match ? match[1] : "";
};

const readMirror = (): string[] => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(stored) ? stored.map(String) : [];
  } catch {
    return [];
  }
};

interface SyncStore {
  unsyncedSiteIds: string[];
  /**
   * Sites whose edit-view SyncNeededBanner the operator closed with its X
   * (PROD-35). Mirrors the About-page alert: closing hides the banner until
   * the next save on that site re-marks it unsynced (or a sync clears it).
   * In-memory only — a reload shows the reminder again, as About does.
   */
  bannerDismissedSiteIds: string[];
  markUnsynced: (siteId: string) => void;
  markSynced: (siteId: string) => void;
  clearAllUnsynced: () => void;
  dismissBanner: (siteId: string) => void;
}

const without = (ids: string[], siteId: string) =>
  ids.filter((id) => id !== siteId);

export const useSyncStore = create<SyncStore>((set, get) => ({
  unsyncedSiteIds: readMirror(),
  bannerDismissedSiteIds: [],
  markUnsynced: (siteId: string) => {
    if (!siteId) return;
    // A fresh save always re-surfaces a dismissed banner, even when the site
    // was already unsynced — same cadence as the About-page alert.
    const bannerDismissedSiteIds = without(
      get().bannerDismissedSiteIds,
      siteId
    );
    const ids = get().unsyncedSiteIds;
    if (ids.includes(siteId)) {
      set({ bannerDismissedSiteIds });
      return;
    }
    const next = [...ids, siteId];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    set({ unsyncedSiteIds: next, bannerDismissedSiteIds });
  },
  markSynced: (siteId: string) => {
    const next = without(get().unsyncedSiteIds, siteId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    set({
      unsyncedSiteIds: next,
      bannerDismissedSiteIds: without(get().bannerDismissedSiteIds, siteId),
    });
  },
  clearAllUnsynced: () => {
    localStorage.setItem(STORAGE_KEY, "[]");
    set({ unsyncedSiteIds: [], bannerDismissedSiteIds: [] });
  },
  dismissBanner: (siteId: string) => {
    if (!siteId) return;
    const ids = get().bannerDismissedSiteIds;
    if (ids.includes(siteId)) return;
    set({ bannerDismissedSiteIds: [...ids, siteId] });
  },
}));

/**
 * A settings change was saved on the site the operator is currently on —
 * mark it unsynced (Sync buttons/banner turn red for THIS site only).
 */
export const markSiteUnsynced = (siteId: string = getSiteIdFromUrl()) =>
  useSyncStore.getState().markUnsynced(siteId);

/** A sync succeeded for the current site — nothing pending for it anymore. */
export const markSiteSynced = (siteId: string = getSiteIdFromUrl()) =>
  useSyncStore.getState().markSynced(siteId);

/** Reactive hook: does the site in the current URL have unsynced changes? */
export const useCurrentSiteSyncNeeded = (): boolean =>
  useSyncStore((s) => s.unsyncedSiteIds.includes(getSiteIdFromUrl()));

/** Operator closed the edit-view banner with its X — hide it until the next save. */
export const dismissSyncBannerForCurrentSite = (
  siteId: string = getSiteIdFromUrl()
) => useSyncStore.getState().dismissBanner(siteId);

/** Reactive hook: was the banner for the site in the current URL closed since the last save? */
export const useCurrentSiteBannerDismissed = (): boolean =>
  useSyncStore((s) => s.bannerDismissedSiteIds.includes(getSiteIdFromUrl()));
